import { lazy, Suspense } from "react";
import { lazyWithRetry } from "../utils/lazyWithRetry";
import { Navigate } from "react-router-dom";
import type { RouteObject } from "react-router-dom";
import { AuthLayout } from "../UI/layouts/AuthLayout";
import { CenteredLayout } from "../UI/layouts/CenteredLayout";
import { Layout } from "../UI/layouts/Layout";
import { Paths } from "./paths";
import { LoadingSpinner } from "./LoadingSpinner";
import { PublicRoute } from "./PublicRoute";
import { ProtectedRoute } from "./ProtectedRoute";
import { RoleRoute } from "./RoleRoute";
import { HideSubscriptionsOnIOS } from "./HideSubscriptionsOnIOS";
import {
  AkindaAiRoute,
  GamesRoute,
  Profile,
  StockReport,
  SupportTickets,
  WebsiteAnalytics,
} from "./router.components";


// Lazy load pages with retry on chunk 404 (after deploy)
const Login = lazyWithRetry(() => import("../UI/pages/Login"));

const Home = lazyWithRetry(() => import("../UI/pages/Home"));
const ForBusiness = lazyWithRetry(() => import("../UI/pages/ForBusiness"));
const Mcp = lazyWithRetry(() => import("../UI/pages/Mcp"));
const Startups = lazyWithRetry(() => import("../UI/pages/Startups"));
const Referral = lazyWithRetry(() => import("../UI/pages/Referral"));
const ReferralRedirect = lazyWithRetry(
  () => import("../UI/pages/Referral/ReferralRedirect"),
);
const AdminReferral = lazyWithRetry(() => import("../UI/pages/AdminReferral"));
const Partners = lazyWithRetry(() => import("../UI/pages/Partners"));
const Contact = lazyWithRetry(() => import("../UI/pages/Contact"));
const CommunityFeed = lazyWithRetry(() => import("../UI/pages/CommunityFeed"));
const PostDetail = lazyWithRetry(() => import("../UI/pages/PostDetail"));
const CommunityProfile = lazyWithRetry(() => import("../UI/pages/CommunityProfile"));
const CommunitySearch = lazyWithRetry(() => import("../UI/pages/CommunitySearch"));
const BlockedUsers = lazyWithRetry(() => import("../UI/pages/BlockedUsers"));
const PrivacyPolicy = lazyWithRetry(() => import("../UI/pages/PrivacyPolicy"));
const TermsAndConditions = lazyWithRetry(
  () => import("../UI/pages/TermsAndConditions"),
);
const Disclosures = lazyWithRetry(() => import("../UI/pages/Disclosures"));
const Users = lazyWithRetry(() => import("../UI/pages/Users"));
const UsersCreate = lazyWithRetry(() => import("../UI/pages/UsersCreate"));
const AdminRolesCreate = lazyWithRetry(
  () => import("../UI/pages/AdminRolesCreate"),
);
const AdminRoles = lazyWithRetry(() => import("../UI/pages/AdminRoles"));
const SignUp = lazyWithRetry(() => import("../UI/pages/SignUp"));
const VerifyEmail = lazyWithRetry(() => import("../UI/pages/VerifyEmail"));
const ForgotPassword = lazyWithRetry(
  () => import("../UI/pages/ForgotPassword"),
);
const CheckYourEmail = lazyWithRetry(
  () => import("../UI/pages/CheckYourEmail"),
);
const SetNewPassword = lazyWithRetry(
  () => import("../UI/pages/SetNewPassword"),
);
const PasswordReset = lazyWithRetry(() => import("../UI/pages/PasswordReset"));
const GoogleExternalAuth = lazyWithRetry(
  () => import("../UI/pages/GoogleExternalAuth"),
);
const PersonalInfo = lazyWithRetry(() => import("../UI/pages/PersonalInfo"));
const StockAnalyses = lazyWithRetry(() => import("../UI/pages/StockAnalyses"));
const TopHalalStock = lazyWithRetry(() => import("../UI/pages/TopHalalStock"));
const ReportDetail = lazyWithRetry(() => import("../UI/pages/ReportDetail"));
const WebinarDetail = lazyWithRetry(() => import("../UI/pages/WebinarDetails"));
const CreateReport = lazyWithRetry(
  () => import("../UI/pages/CreateStockAnalys"),
);
const EditReport = lazyWithRetry(() => import("../UI/pages/EditReport"));
const Reports = lazyWithRetry(() => import("../UI/pages/Reports"));
const Finance = lazyWithRetry(() => import("../UI/pages/Finance"));
const Articles = lazyWithRetry(() => import("../UI/pages/Articles"));
const Webinars = lazyWithRetry(() => import("../UI/pages/Webinar"));
const CreateWebinar = lazyWithRetry(() => import("../UI/pages/CreateWebinar"));
const CreateCourse = lazyWithRetry(() => import("../UI/pages/CreateCourse"));
const CourseStats = lazyWithRetry(() => import("../UI/pages/CourseStats"));
const CreateArticle = lazyWithRetry(() => import("../UI/pages/CreateArticle"));
const Notifications = lazyWithRetry(() => import("../UI/pages/Notifications"));
const CreateNotifications = lazyWithRetry(
  () => import("../UI/pages/CreateNotifications"),
);
const NotificationSettings = lazyWithRetry(
  () => import("../UI/pages/NotificationSettings"),
);
const SubscriptionMatrix = lazyWithRetry(
  () => import("../UI/pages/SubscriptionMatrix"),
);
const PaymentDetails = lazyWithRetry(
  () => import("../UI/pages/PaymentDetails"),
);
const SubscriptionResult = lazyWithRetry(
  () => import("../UI/pages/SubscriptionResult"),
);
const Subscribe = lazyWithRetry(() => import("../UI/pages/Subscribe"));
const Subscriptions = lazyWithRetry(() => import("../UI/pages/Subscriptions"));
const Academy = lazyWithRetry(() => import("../UI/pages/Academy"));
const Portfolios = lazyWithRetry(() => import("../UI/pages/Portfolios"));
const ReadyMadePortfolioStocks = lazyWithRetry(
  () => import("../UI/pages/ReadyMadePortfolio/ReadyMadePortfolioStocks"),
);
const ReadyMadePortfolioDetail = lazyWithRetry(
  () => import("../UI/pages/ReadyMadePortfolio/ReadyMadePortfolioDetail"),
);
const PublicPortfolio = lazyWithRetry(
  () => import("../UI/pages/PublicPortfolio"),
);
const CourseInfo = lazyWithRetry(() => import("../UI/pages/CourseInfo"));
const CourseDashboard = lazyWithRetry(
  () => import("../UI/pages/CourseDashboard"),
);
const MarketNews = lazyWithRetry(() => import("../UI/pages/MarketNews"));
const Summary = lazyWithRetry(() => import("../UI/pages/Summary"));
const NotFound = lazyWithRetry(() => import("../UI/pages/NotFound"));
const ArticleCategories = lazyWithRetry(
  () => import("../UI/pages/ArticleCategories"),
);
const CreateArticleCategory = lazyWithRetry(
  () => import("../UI/pages/CreateArticleCategory"),
);
const Cards = lazyWithRetry(() => import("../UI/pages/Cards"));
const Promocodes = lazy(() => import("../UI/pages/Promocodes"));
const PromocodesDetail = lazy(
  () => import("../UI/pages/Promocodes/components/PromocodesDetail"),
);
const PromocodesCreate = lazy(
  () => import("../UI/pages/Promocodes/components/PromocodesCreate"),
);
const OnboardingQuestions = lazyWithRetry(
  () => import("../UI/pages/OnboardingQuestions"),
);
const OnboardingQuestionCreate = lazyWithRetry(
  () =>
    import(
      "../UI/pages/OnboardingQuestions/components/OnboardingQuestionCreate"
    ),
);
const OnboardingStats = lazyWithRetry(
  () => import("../UI/pages/OnboardingQuestions/components/OnboardingStats"),
);

const DashboardPage = lazyWithRetry(() => import("../UI/pages/Dashboard"));

/**
 * The client-only part of the app, rendered as descendant routes by
 * src/routes/spa.tsx after hydration. Server-rendered SEO routes are real
 * route modules in src/routes.ts instead.
 */
export const appRoutes: RouteObject[] = [
  {
    element: <Layout />,
    children: [
      {
        path: Paths.PUBLIC_PORTFOLIO,
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <PublicPortfolio />
          </Suspense>
        ),
      },
      {
        path: Paths.PROMOCODES,
        element: (
          <RoleRoute roles={["super-admin", "admin"]}>
            <Suspense fallback={<LoadingSpinner />}>
              <Promocodes />
            </Suspense>
          </RoleRoute>
        ),
      },
      {
        path: Paths.PROMOCODES_DETAILS_ID,
        element: (
          <RoleRoute roles={["super-admin", "admin"]}>
            <Suspense fallback={<LoadingSpinner />}>
              <PromocodesDetail />
            </Suspense>
          </RoleRoute>
        ),
      },
      {
        path: Paths.PROMOCODES_CREATE,
        element: (
          <RoleRoute roles={["super-admin", "admin"]}>
            <Suspense fallback={<LoadingSpinner />}>
              <PromocodesCreate />
            </Suspense>
          </RoleRoute>
        ),
      },
      {
        path: Paths.PROMOCODES_CREATE_ID,
        element: (
          <RoleRoute roles={["super-admin", "admin"]}>
            <Suspense fallback={<LoadingSpinner />}>
              <PromocodesCreate />
            </Suspense>
          </RoleRoute>
        ),
      },
      {
        path: Paths.SUBSCRIBE_PLAN_TYPE,
        element: (
          <HideSubscriptionsOnIOS>
            <Suspense fallback={<LoadingSpinner />}>
              <Subscribe />
            </Suspense>
          </HideSubscriptionsOnIOS>
        ),
      },
      {
        path: Paths.HOME,
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <Home />
          </Suspense>
        ),
      },
      // Paths.STOCK_SCREENER is server-rendered: src/routes/stock-screener.tsx.
      // Paths.ETF_SCREENER is server-rendered: src/routes/etf-screener.tsx.
      // Paths.ETF is server-rendered: src/routes/etf.tsx.
      {
        path: Paths.FOR_BUSINESS,
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <ForBusiness />
          </Suspense>
        ),
      },
      {
        path: Paths.MCP,
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <Mcp />
          </Suspense>
        ),
      },
      {
        path: Paths.STARTUPS,
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <Startups />
          </Suspense>
        ),
      },
      {
        path: Paths.REFERRAL,
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <Referral />
          </Suspense>
        ),
      },
      {
        path: Paths.REFERRAL_CODE,
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <ReferralRedirect />
          </Suspense>
        ),
      },
      {
        path: Paths.PARTNERS,
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <Partners />
          </Suspense>
        ),
      },
      {
        path: Paths.CONTACT,
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <Contact />
          </Suspense>
        ),
      },
      // Community requires authentication: unauthenticated visitors are
      // redirected to /login, consistent with other gated pages.
      {
        element: <ProtectedRoute />,
        children: [
          {
            path: Paths.COMMUNITY,
            element: <Navigate to={Paths.COMMUNITY_FEED} replace />,
          },
          {
            path: Paths.COMMUNITY_FEED,
            element: (
              <Suspense fallback={<LoadingSpinner />}>
                <CommunityFeed />
              </Suspense>
            ),
          },
          {
            path: Paths.COMMUNITY_POST,
            element: (
              <Suspense fallback={<LoadingSpinner />}>
                <PostDetail />
              </Suspense>
            ),
          },
          {
            path: Paths.COMMUNITY_PROFILE,
            element: (
              <Suspense fallback={<LoadingSpinner />}>
                <CommunityProfile />
              </Suspense>
            ),
          },
          {
            path: Paths.COMMUNITY_SEARCH,
            element: (
              <Suspense fallback={<LoadingSpinner />}>
                <CommunitySearch />
              </Suspense>
            ),
          },
          {
            path: Paths.COMMUNITY_BLOCKED,
            element: (
              <Suspense fallback={<LoadingSpinner />}>
                <BlockedUsers />
              </Suspense>
            ),
          },
        ],
      },
      {
        path: Paths.PRIVACY_POLICY,
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <PrivacyPolicy />
          </Suspense>
        ),
      },
      {
        path: Paths.TERMS_AND_CONDITIONS,
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <TermsAndConditions />
          </Suspense>
        ),
      },
      {
        path: Paths.DISCLOSURES,
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <Disclosures />
          </Suspense>
        ),
      },
      {
        path: Paths.SUMMARY,
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <Summary />
          </Suspense>
        ),
      },
      // Paths.SYMBOL is server-rendered: src/routes/symbol.tsx.
      {
        path: Paths.MARKET_NEWS,
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <MarketNews />
          </Suspense>
        ),
      },
      {
        path: Paths.STOCK_ANALYSES,
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <StockAnalyses />
          </Suspense>
        ),
      },
      {
        path: Paths.SCREENING,
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <StockAnalyses />
          </Suspense>
        ),
      },
      {
        path: Paths.INSIDER_BY_SECTOR,
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <StockAnalyses />
          </Suspense>
        ),
      },
      {
        path: Paths.DIVIDEND,
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <StockAnalyses />
          </Suspense>
        ),
      },
      {
        path: Paths.MY_PORTFOLIO,
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <Portfolios />
          </Suspense>
        ),
      },
      {
        path: Paths.READY_MADE_PORTFOLIO_ID,
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <ReadyMadePortfolioDetail />
          </Suspense>
        ),
      },
      {
        path: Paths.READY_MADE_PORTFOLIO_STOCKS,
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <ReadyMadePortfolioStocks />
          </Suspense>
        ),
      },
      {
        path: Paths.PORTFOLIO_BACKTESTING,
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <StockAnalyses />
          </Suspense>
        ),
      },
      {
        path: Paths.PORTFOLIO_TRACKING,
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <StockAnalyses />
          </Suspense>
        ),
      },
      {
        path: Paths.AKINDA_AI,
        element: <AkindaAiRoute />,
      },
      {
        path: Paths.GAMES,
        element: <GamesRoute />,
      },
      {
        path: Paths.ARTICLES,
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <Articles />
          </Suspense>
        ),
      },
      // Paths.ARTICLE_DETAIL is server-rendered: src/routes/article.tsx.
      {
        path: Paths.ACADEMY,
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <Academy />
          </Suspense>
        ),
      },
      {
        path: Paths.COURSE_INFO_ID,
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <CourseInfo />
          </Suspense>
        ),
      },
      {
        path: Paths.COURSE_DASHBOARD_ID,
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <CourseDashboard />
          </Suspense>
        ),
      },
      {
        path: Paths.WEBINARS,
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <Webinars />
          </Suspense>
        ),
      },
      {
        path: Paths.WEBINAR_DETAIL_ID,
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <WebinarDetail />
          </Suspense>
        ),
      },
    ],
  },
  {
    element: <PublicRoute />,
    children: [
      {
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <AuthLayout />
          </Suspense>
        ),
        children: [
          {
            path: Paths.LOGIN,
            element: (
              <Suspense fallback={<LoadingSpinner />}>
                <Login />
              </Suspense>
            ),
          },
          {
            path: Paths.SIGN_UP,
            element: (
              <Suspense fallback={<LoadingSpinner />}>
                <SignUp />
              </Suspense>
            ),
          },
          {
            path: Paths.VERIFY_EMAIL,
            element: (
              <Suspense fallback={<LoadingSpinner />}>
                <VerifyEmail />
              </Suspense>
            ),
          },
        ],
      },
      {
        element: (
          <Suspense fallback={<LoadingSpinner />}>
            <CenteredLayout />
          </Suspense>
        ),
        children: [
          {
            path: Paths.FORGOT_PASSWORD,
            element: (
              <Suspense fallback={<LoadingSpinner />}>
                <ForgotPassword />
              </Suspense>
            ),
          },
          {
            path: Paths.CHECK_YOUREMAIL,
            element: (
              <Suspense fallback={<LoadingSpinner />}>
                <CheckYourEmail />
              </Suspense>
            ),
          },
          {
            path: Paths.SET_NEW_PASSWORD,
            element: (
              <Suspense fallback={<LoadingSpinner />}>
                <SetNewPassword />
              </Suspense>
            ),
          },
          {
            path: Paths.PASSWORD_RESET,
            element: (
              <Suspense fallback={<LoadingSpinner />}>
                <PasswordReset />
              </Suspense>
            ),
          },
          {
            path: Paths.GOOGLE_EXTERNAL_AUTH,
            element: (
              <Suspense fallback={<LoadingSpinner />}>
                <GoogleExternalAuth />
              </Suspense>
            ),
          },
        ],
      },
    ],
  },
  {
    element: <ProtectedRoute />,
    children: [
      {
        element: <Layout />,
        children: [
          {
            path: Paths.DASHBOARD,
            element: (
              <RoleRoute roles={["super-admin"]}>
                <Suspense fallback={<LoadingSpinner />}>
                  <DashboardPage />
                </Suspense>
              </RoleRoute>
            ),
          },
          {
            path: Paths.USERS,
            element: (
              <RoleRoute roles={["super-admin", "admin", "support"]}>
                <Suspense fallback={<LoadingSpinner />}>
                  <Users />
                </Suspense>
              </RoleRoute>
            ),
          },
          {
            path: Paths.USERS_CREATE,
            element: (
              <RoleRoute roles={["super-admin", "admin"]}>
                <Suspense fallback={<LoadingSpinner />}>
                  <UsersCreate />
                </Suspense>
              </RoleRoute>
            ),
          },
          {
            path: Paths.USERS_DETAILS_ID,
            element: (
              <RoleRoute roles={["super-admin", "admin"]}>
                <Suspense fallback={<LoadingSpinner />}>
                  <UsersCreate />
                </Suspense>
              </RoleRoute>
            ),
          },
          {
            path: Paths.PROFILE,
            element: (
              <RoleRoute roles={["super-admin"]}>
                <Suspense fallback={<LoadingSpinner />}>
                  <Profile />
                </Suspense>
              </RoleRoute>
            ),
          },
          {
            path: Paths.ADMIN_ROLES,
            element: (
              <RoleRoute roles={["super-admin"]}>
                <Suspense fallback={<LoadingSpinner />}>
                  <AdminRoles />
                </Suspense>
              </RoleRoute>
            ),
          },
          {
            path: Paths.ADMIN_ROLES_CREATE,
            element: (
              <RoleRoute roles={["super-admin"]}>
                <Suspense fallback={<LoadingSpinner />}>
                  <AdminRolesCreate />
                </Suspense>
              </RoleRoute>
            ),
          },
          {
            path: Paths.ADMIN_ROLES_DETAILS_ID,
            element: (
              <RoleRoute roles={["super-admin"]}>
                <Suspense fallback={<LoadingSpinner />}>
                  <AdminRolesCreate />
                </Suspense>
              </RoleRoute>
            ),
          },
          {
            path: Paths.SUBSCRIPTIONS,
            // Any signed-in account may manage its own subscription. Staff roles
            // (analitik, support, content-manager, editor, paid-subscriber) used to be
            // bounced to /403 here, which every "start your trial" link runs into.
            element: (
              <HideSubscriptionsOnIOS>
                <Suspense fallback={<LoadingSpinner />}>
                  <Subscriptions />
                </Suspense>
              </HideSubscriptionsOnIOS>
            ),
          },
          {
            path: Paths.SUBSCRIPTION_MATRIX,
            element: (
              <HideSubscriptionsOnIOS>
                <RoleRoute roles={["super-admin"]}>
                  <Suspense fallback={<LoadingSpinner />}>
                    <SubscriptionMatrix />
                  </Suspense>
                </RoleRoute>
              </HideSubscriptionsOnIOS>
            ),
          },
          {
            path: Paths.PAYMENT_DETAILS,
            element: (
              <HideSubscriptionsOnIOS>
                <Suspense fallback={<LoadingSpinner />}>
                  <PaymentDetails />
                </Suspense>
              </HideSubscriptionsOnIOS>
            ),
          },
          {
            path: Paths.SUBSCRIPTION_RESULT,
            element: (
              <HideSubscriptionsOnIOS>
                <Suspense fallback={<LoadingSpinner />}>
                  <SubscriptionResult />
                </Suspense>
              </HideSubscriptionsOnIOS>
            ),
          },
          {
            path: Paths.WEBSITE_ANALYTICS,
            element: (
              <RoleRoute roles={["super-admin", "admin"]}>
                <Suspense fallback={<LoadingSpinner />}>
                  <WebsiteAnalytics />
                </Suspense>
              </RoleRoute>
            ),
          },
          {
            path: Paths.STOCK_ANALYSES,
            element: (
              <RoleRoute roles={["super-admin", "admin", "editor"]}>
                <Suspense fallback={<LoadingSpinner />}>
                  <StockAnalyses />
                </Suspense>
              </RoleRoute>
            ),
          },
          {
            path: Paths.STOCK_REPORT,
            element: (
              <RoleRoute roles={["super-admin", "admin", "editor"]}>
                <Suspense fallback={<LoadingSpinner />}>
                  <StockReport />
                </Suspense>
              </RoleRoute>
            ),
          },
          {
            path: Paths.PORTFOLIOS,
            element: (
              <RoleRoute roles={["super-admin", "admin", "editor", "user"]}>
                <Suspense fallback={<LoadingSpinner />}>
                  <Portfolios />
                </Suspense>
              </RoleRoute>
            ),
          },
          {
            path: Paths.FINANCE,
            element: (
              <RoleRoute roles={["super-admin"]}>
                <Suspense fallback={<LoadingSpinner />}>
                  <Finance />
                </Suspense>
              </RoleRoute>
            ),
          },
          {
            path: Paths.ADMIN_REFERRAL,
            element: (
              <RoleRoute roles={["super-admin", "admin"]}>
                <Suspense fallback={<LoadingSpinner />}>
                  <AdminReferral />
                </Suspense>
              </RoleRoute>
            ),
          },
          {
            path: Paths.ARTICLES,
            element: (
              <RoleRoute roles={["super-admin", "admin", "editor"]}>
                <Suspense fallback={<LoadingSpinner />}>
                  <Articles />
                </Suspense>
              </RoleRoute>
            ),
          },
          {
            path: Paths.ARTICLES_CREATE,
            element: (
              <RoleRoute roles={["super-admin", "admin", "editor"]}>
                <Suspense fallback={<LoadingSpinner />}>
                  <CreateArticle />
                </Suspense>
              </RoleRoute>
            ),
          },
          {
            path: Paths.ARTICLES_CREATE_ID,
            element: (
              <RoleRoute roles={["super-admin", "admin", "editor"]}>
                <Suspense fallback={<LoadingSpinner />}>
                  <CreateArticle />
                </Suspense>
              </RoleRoute>
            ),
          },
          {
            path: Paths.WEBINAR_DETAIL_ID,
            element: (
              <RoleRoute roles={["super-admin", "admin", "user", "analitik"]}>
                <Suspense fallback={<LoadingSpinner />}>
                  <WebinarDetail />
                </Suspense>
              </RoleRoute>
            ),
          },
          {
            path: Paths.WEBINARS,
            element: (
              <RoleRoute roles={["super-admin", "admin", "user", "analitik"]}>
                <Suspense fallback={<LoadingSpinner />}>
                  <Webinars />
                </Suspense>
              </RoleRoute>
            ),
          },
          {
            path: Paths.WEBINARS_CREATE,
            element: (
              <RoleRoute roles={["super-admin", "admin", "analitik"]}>
                <Suspense fallback={<LoadingSpinner />}>
                  <CreateWebinar />
                </Suspense>
              </RoleRoute>
            ),
          },
          {
            path: Paths.WEBINARS_CREATE_ID,
            element: (
              <RoleRoute roles={["super-admin", "admin", "analitik"]}>
                <Suspense fallback={<LoadingSpinner />}>
                  <CreateWebinar />
                </Suspense>
              </RoleRoute>
            ),
          },
          {
            path: Paths.SUPPORT_TICKETS,
            element: (
              <RoleRoute roles={["super-admin", "admin", "support", "user"]}>
                <Suspense fallback={<LoadingSpinner />}>
                  <SupportTickets />
                </Suspense>
              </RoleRoute>
            ),
          },
          {
            path: Paths.NOTIFICATIONS,
            element: (
              <RoleRoute roles={["super-admin", "user", "admin"]}>
                <Suspense fallback={<LoadingSpinner />}>
                  <Notifications />
                </Suspense>
              </RoleRoute>
            ),
          },
          {
            path: Paths.NOTIFICATIONS_CREATE,
            element: (
              <RoleRoute roles={["super-admin", "admin"]}>
                <Suspense fallback={<LoadingSpinner />}>
                  <CreateNotifications />
                </Suspense>
              </RoleRoute>
            ),
          },
          {
            path: Paths.NOTIFICATIONS_DETAILS_ID,
            element: (
              <RoleRoute roles={["super-admin", "admin"]}>
                <Suspense fallback={<LoadingSpinner />}>
                  <CreateNotifications />
                </Suspense>
              </RoleRoute>
            ),
          },
          {
            path: Paths.NOTIFICATIONS_SETTINGS,
            element: (
              <RoleRoute roles={["super-admin", "admin"]}>
                <Suspense fallback={<LoadingSpinner />}>
                  <NotificationSettings />
                </Suspense>
              </RoleRoute>
            ),
          },
          {
            path: Paths.ONBOARDING_QUESTIONS,
            element: (
              <RoleRoute roles={["super-admin", "admin", "editor"]}>
                <Suspense fallback={<LoadingSpinner />}>
                  <OnboardingQuestions />
                </Suspense>
              </RoleRoute>
            ),
          },
          {
            path: Paths.ONBOARDING_QUESTIONS_CREATE,
            element: (
              <RoleRoute roles={["super-admin", "admin", "editor"]}>
                <Suspense fallback={<LoadingSpinner />}>
                  <OnboardingQuestionCreate />
                </Suspense>
              </RoleRoute>
            ),
          },
          {
            path: Paths.ONBOARDING_QUESTIONS_CREATE_ID,
            element: (
              <RoleRoute roles={["super-admin", "admin", "editor"]}>
                <Suspense fallback={<LoadingSpinner />}>
                  <OnboardingQuestionCreate />
                </Suspense>
              </RoleRoute>
            ),
          },
          {
            path: Paths.ONBOARDING_STATS,
            element: (
              <RoleRoute roles={["super-admin", "admin", "editor"]}>
                <Suspense fallback={<LoadingSpinner />}>
                  <OnboardingStats />
                </Suspense>
              </RoleRoute>
            ),
          },
          {
            path: Paths.PERSONAL_INFO,
            element: (
              <Suspense fallback={<LoadingSpinner />}>
                <PersonalInfo />
              </Suspense>
            ),
          },
          {
            path: Paths.MY_CARDS,
            element: (
              <Suspense fallback={<LoadingSpinner />}>
                <Cards />
              </Suspense>
            ),
          },
          {
            path: Paths.TOP_HALAL_STOCKS,
            element: (
              <RoleRoute roles={["super-admin", "admin", "editor"]}>
                <Suspense fallback={<LoadingSpinner />}>
                  <TopHalalStock />
                </Suspense>
              </RoleRoute>
            ),
          },
          {
            path: Paths.REPORT_DETAIL_ID,
            element: (
              <RoleRoute roles={["admin", "super-admin", "user", "analitik"]}>
                <Suspense fallback={<LoadingSpinner />}>
                  <ReportDetail />
                </Suspense>
              </RoleRoute>
            ),
          },
          {
            path: Paths.EDIT_REPORT_ID,
            element: (
              <RoleRoute roles={["super-admin", "admin", "editor", "analitik"]}>
                <Suspense fallback={<LoadingSpinner />}>
                  <EditReport />
                </Suspense>
              </RoleRoute>
            ),
          },
          {
            path: Paths.ARTICLE_CATEGORIES,
            element: (
              <RoleRoute roles={["super-admin", "admin", "editor"]}>
                <Suspense fallback={<LoadingSpinner />}>
                  <ArticleCategories />
                </Suspense>
              </RoleRoute>
            ),
          },
          {
            path: Paths.ARTICLE_CATEGORIES_CREATE,
            element: (
              <RoleRoute roles={["super-admin", "admin", "editor"]}>
                <Suspense fallback={<LoadingSpinner />}>
                  <CreateArticleCategory />
                </Suspense>
              </RoleRoute>
            ),
          },
          {
            path: Paths.ARTICLE_CATEGORIES_CREATE_ID,
            element: (
              <RoleRoute roles={["super-admin", "admin", "editor"]}>
                <Suspense fallback={<LoadingSpinner />}>
                  <CreateArticleCategory />
                </Suspense>
              </RoleRoute>
            ),
          },
          {
            path: Paths.REPORTS,
            element: (
              <Suspense fallback={<LoadingSpinner />}>
                <Reports />
              </Suspense>
            ),
          },
          {
            path: Paths.REPORTS_CREATE,
            element: (
              <Suspense fallback={<LoadingSpinner />}>
                <CreateReport />
              </Suspense>
            ),
          },
          {
            path: Paths.CREATE_REPORT_ID,
            element: (
              <RoleRoute roles={["super-admin", "admin", "editor", "analitik"]}>
                <Suspense fallback={<LoadingSpinner />}>
                  <CreateReport />
                </Suspense>
              </RoleRoute>
            ),
          },
          {
            path: Paths.REPORTS_DETAILS_ID,
            element: (
              <Suspense fallback={<LoadingSpinner />}>
                <ReportDetail />
              </Suspense>
            ),
          },
          {
            path: Paths.CREATE_REPORT,
            element: (
              <Suspense fallback={<LoadingSpinner />}>
                <CreateReport />
              </Suspense>
            ),
          },
          {
            path: Paths.ACADEMY,
            element: (
              <RoleRoute roles={["super-admin", "admin", "editor", "support"]}>
                <Suspense fallback={<LoadingSpinner />}>
                  <Academy />
                </Suspense>
              </RoleRoute>
            ),
          },
          {
            path: Paths.COURSE_INFO_ID,
            element: (
              <RoleRoute roles={["super-admin", "admin", "editor", "support"]}>
                <Suspense fallback={<LoadingSpinner />}>
                  <CourseInfo />
                </Suspense>
              </RoleRoute>
            ),
          },
          {
            path: Paths.COURSES_DETAIL_ID,
            element: (
              <RoleRoute roles={["super-admin", "admin", "support"]}>
                <Suspense fallback={<LoadingSpinner />}>
                  <WebinarDetail />
                </Suspense>
              </RoleRoute>
            ),
          },
          {
            path: Paths.COURSES_CREATE_ID,
            element: (
              <RoleRoute roles={["super-admin", "admin", "editor", "support"]}>
                <Suspense fallback={<LoadingSpinner />}>
                  <CreateCourse />
                </Suspense>
              </RoleRoute>
            ),
          },
          {
            path: Paths.COURSES_CREATE,
            element: (
              <RoleRoute roles={["super-admin", "admin", "editor", "support"]}>
                <Suspense fallback={<LoadingSpinner />}>
                  <CreateCourse />
                </Suspense>
              </RoleRoute>
            ),
          },
          {
            path: Paths.COURSES_STATS_ID,
            element: (
              <RoleRoute roles={["super-admin", "admin", "editor"]}>
                <Suspense fallback={<LoadingSpinner />}>
                  <CourseStats />
                </Suspense>
              </RoleRoute>
            ),
          },
        ],
      },
    ],
  },
  {
    path: Paths.NOT_FOUND,
    element: (
      <Suspense fallback={<LoadingSpinner />}>
        <NotFound />
      </Suspense>
    ),
  },
];
